import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { Send, Loader2, AlertCircle, Paperclip, X, FileText, Image, Share2, MessageSquare, Trash2, Plus, ChevronLeft, Menu, Copy, Check, GitBranch, ClipboardList, Download, Volume2, VolumeX, Mic, MicOff, Palette, Pause, Play, LayoutGrid, Pencil, Sparkles, ChevronDown } from 'lucide-react';
import { speak as ttsSpeak, stop as ttsStop, pause as ttsPause, resume as ttsResume, isSpeechSupported, getSelectedTextWithin } from '../utils/tts';
import { hasBrowserSTT, hasMediaRecorder, isVoiceInputSupported, startBrowserSTT, startWhisperRecording } from '../utils/voiceInput';
import { chatApi, uploadApi, shareApi, authApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { lazy, Suspense } from 'react';
const MindMapModal = lazy(() => import('../components/MindMapModal'));
const InfographicMindMap = lazy(() => import('../components/InfographicMindMap'));

// Max characters to render before showing "Show more" (prevents browser freeze on huge responses)
const MSG_TRUNCATE_LIMIT = 15000;

// Extract Review List section from markdown content
function extractReviewList(content) {
  if (!content) return { body: content, reviewList: null };
  // Match various Review List header formats: with/without emoji, bold, colon, ##, etc.
  const reviewMatch = content.match(/((?:#{1,3}\s*)?📋?\s*\*{0,2}Review\s*List\*{0,2}:?\s*[\s\S]*?)$/i);
  if (!reviewMatch) return { body: content, reviewList: null };
  const body = content.slice(0, reviewMatch.index).trimEnd();
  const reviewList = reviewMatch[1].trim();
  // Detect broken/truncated tables: check if there are actual data rows (not just dashes/separators)
  const lines = reviewList.split('\n').filter(l => l.trim());
  const dataLines = lines.filter(l => l.includes('|') && l.replace(/[|\s:*-]/g, '').length > 10);
  if (dataLines.length < 2) return { body: content, reviewList: null };
  return { body, reviewList };
}

// Renders a chat attachment image. If the image file is missing on disk (404 — typical for
// archived uploads older than 60 days), shows a friendly archived placeholder instead of a
// broken-image icon. The chat text response above this is always preserved.
function AttachmentImage({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-xs">
        <FileText className="w-4 h-4 opacity-70" />
        <span>📦 Archived — original attachment is no longer available</span>
      </div>
    );
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        className="max-w-[200px] max-h-[200px] rounded-lg object-cover border border-white/20"
      />
    </a>
  );
}

// Techniques a student can retry the same question with. Keys map to the
// actual technique slugs in the DB where applicable, so TechniqueChat can
// exclude the current one. Prompts are prepended to the retry message.
const RETRY_TECHNIQUES = [
  { key: 'link-association-for-long-answers', label: 'Link & Association',    prompt: 'Teach the following using the LINK & ASSOCIATION technique — connect each fact to the next with a vivid, chained image. Keep the same concept scene throughout.' },
  { key: 'me-to-me-technique',                label: 'Me to Me',              prompt: 'Teach the following using the ME-TO-ME technique — the student stands in front of a mirror, each body part / layout position becomes a hook for one fact.' },
  { key: 'memory-palace',                     label: 'Memory Palace',         prompt: 'Teach the following using the MEMORY PALACE technique — place each fact at a specific location in the student\'s palace rooms (hooks section only). Keep conceptual story in a non-palace setting.' },
  { key: 'peg-system',                        label: 'Peg System',            prompt: 'Teach the following using the PEG SYSTEM — attach each fact to a numbered peg image. Follow the 3-digit peg rule (e.g. 938 → 09 + 38). Keep the same concept scene throughout.' },
  { key: 'finishing-pages',                   label: 'Finishing Pages',       prompt: 'Teach the following using the FINISHING PAGES - KEYWORDS FILTERING technique — pull out just the critical keywords, group them, then build minimal hooks on top.' },
  { key: 'mental-summary-charts',             label: 'Mental Summary Chart',  prompt: 'Teach the following using the MENTAL SUMMARY CHART (MSC) technique — give substitutions, keywords, and a short pictogram-ready story suitable for a visual poster.' },
  { key: 'story',                             label: 'Story Method',          prompt: 'Teach the following using the STORY METHOD — weave all facts into one short, memorable story. Keep the same concept scene throughout.' },
  { key: 'substitution',                      label: 'Substitution Method',   prompt: 'Teach the following using the SUBSTITUTION method — convert every difficult word / name / formula into a familiar vivid image, then link them. Apply the mandatory substitution rules.' },
  { key: 'number-shape-rhyme',                label: 'Number Shape / Rhyme',  prompt: 'Teach the following using the NUMBER SHAPE / NUMBER RHYME technique — attach each fact to the shape or rhyme of its number.' },
];

// Inline chip row shown under every assistant reply. Each chip re-teaches
// the same question with a different technique (costs 1 question).
function TechniqueChips({ onPick, disabled, activeIdx, idx, excludeKey }) {
  const isActive = activeIdx === idx;
  return (
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3 h-3 text-purple-500" />
        <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Try another technique
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500">· 1 question each</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {RETRY_TECHNIQUES.filter(t => t.key !== excludeKey).map(t => (
          <button
            key={t.key}
            onClick={() => onPick(t)}
            disabled={disabled}
            className="px-2.5 py-1 text-xs rounded-full border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300 dark:border-purple-900 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isActive && disabled ? (
              <span className="flex items-center"><Loader2 className="w-3 h-3 mr-1 animate-spin" />{t.label}</span>
            ) : t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Memoized message bubble to prevent re-rendering all messages on state changes
const MessageBubble = memo(function MessageBubble({ msg, idx, copiedIdx, onCopy, onMindMap, onInfographic, onReviewList, reviewLoading, speakingIdx, speakingPaused, onSpeak, onImageMindMap, imageMindMapLoading, onTryTechnique, retryLoading }) {
  const [expanded, setExpanded] = useState(false);
  const { body } = msg.role === 'assistant' ? extractReviewList(msg.content) : { body: msg.content };
  const isLong = msg.role === 'assistant' && body && body.length > MSG_TRUNCATE_LIMIT;
  const displayContent = (isLong && !expanded) ? body.slice(0, MSG_TRUNCATE_LIMIT) + '\n\n---' : body;

  return (
    <div className={`flex message-fade-in ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        msg.role === 'user'
          ? 'bg-primary-600 text-white'
          : msg.isError
            ? 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-gray-900 dark:text-gray-100'
            : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-gray-100'
      }`}>
        {msg.isError && (
          <div className="flex items-center text-red-600 mb-2">
            <AlertCircle className="w-4 h-4 mr-2" />
            <span className="font-medium">Limit Reached</span>
          </div>
        )}
        {msg.role === 'user' || msg.isError ? (
          <div>
            {msg.images && msg.images.length > 0 && (
              <div className={`flex flex-wrap gap-2 ${msg.content ? 'mb-2' : ''}`}>
                {msg.images.map((img, i) => (
                  <AttachmentImage key={i} src={img.url} alt={img.name} />
                ))}
              </div>
            )}
            {msg.pdfNames && msg.pdfNames.length > 0 && (
              <div className={`flex flex-wrap gap-1 ${msg.content ? 'mb-2' : ''}`}>
                {msg.pdfNames.map((name, i) => (
                  <span key={i} className="inline-flex items-center text-xs bg-white/20 rounded px-2 py-1">
                    <FileText className="w-3 h-3 mr-1" /> {name}
                  </span>
                ))}
              </div>
            )}
            {msg.content && (
              <p className={`whitespace-pre-wrap ${msg.isError ? 'text-red-700' : ''}`}>{msg.content}</p>
            )}
          </div>
        ) : (
          <div data-msg-idx={idx}>
            <div className="prose prose-sm max-w-none prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0
              [&_table]:w-full [&_table]:border-collapse [&_table]:border [&_table]:border-gray-200 [&_table]:my-2
              [&_th]:bg-gray-50 [&_th]:font-semibold [&_th]:px-2 [&_th]:py-1 [&_th]:border [&_th]:border-gray-200 [&_th]:text-left [&_th]:text-xs
              [&_td]:px-2 [&_td]:py-1 [&_td]:border [&_td]:border-gray-200 [&_td]:text-xs">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
            </div>
            {isLong && !expanded && (
              <button
                onClick={() => setExpanded(true)}
                className="mt-2 text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                Show full response ({Math.round(msg.content.length / 1000)}K chars)
              </button>
            )}
            <div className="mt-2 flex items-center space-x-3">
              <button
                onClick={() => onCopy(idx, msg.content)}
                className="flex items-center text-xs text-gray-400 hover:text-gray-600 transition-colors"
                title="Copy response"
              >
                {copiedIdx === idx ? <><Check className="w-3 h-3 mr-1" /> Copied!</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
              </button>
              <button
                onClick={() => onMindMap(idx)}
                className="flex items-center text-xs text-gray-400 hover:text-indigo-600 transition-colors"
                title="View as Mind Map"
              >
                <GitBranch className="w-3 h-3 mr-1" /> Mind Map
              </button>
              <button
                onClick={() => onReviewList(idx, msg.content)}
                disabled={reviewLoading === idx}
                className="flex items-center text-xs text-gray-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
                title="View Review List"
              >
                {reviewLoading === idx ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                ) : (
                  <><ClipboardList className="w-3 h-3 mr-1" /> Review</>
                )}
              </button>
              {isSpeechSupported() && (
                <>
                  {speakingIdx === idx ? (
                    <>
                      <button
                        onClick={() => onSpeak(idx, msg.content, 'pause')}
                        className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                        title={speakingPaused ? 'Resume' : 'Pause'}
                      >
                        {speakingPaused ? (
                          <><Play className="w-3 h-3 mr-1" /> Resume</>
                        ) : (
                          <><Pause className="w-3 h-3 mr-1" /> Pause</>
                        )}
                      </button>
                      <button
                        onClick={() => onSpeak(idx, msg.content, 'stop')}
                        className="flex items-center text-xs text-gray-400 hover:text-red-600 transition-colors"
                        title="Stop"
                      >
                        <VolumeX className="w-3 h-3 mr-1" /> Stop
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => onSpeak(idx, msg.content)}
                      className="flex items-center text-xs text-gray-400 hover:text-blue-600 transition-colors"
                      title="Listen (select text first to read only that portion)"
                    >
                      <Volume2 className="w-3 h-3 mr-1" /> Listen
                    </button>
                  )}
                </>
              )}
              <button
                onClick={() => onInfographic(idx)}
                className="flex items-center text-xs text-gray-400 hover:text-pink-600 transition-colors"
                title="View as Visual Mind Map (costs 2 questions)"
              >
                <Palette className="w-3 h-3 mr-1" /> Visual
              </button>
            </div>
            <TechniqueChips
              onPick={(t) => onTryTechnique(idx, t)}
              disabled={retryLoading !== null}
              activeIdx={retryLoading}
              idx={idx}
            />
          </div>
        )}
        {msg.code === 'QUOTA_EXHAUSTED' && (
          <Link to="/recharge" className="mt-2 inline-block text-primary-600 hover:underline text-sm">
            Buy more questions
          </Link>
        )}
        {(msg.code === 'DAILY_LIMIT_REACHED' || msg.code === 'TRIAL_EXPIRED') && !msg.registrationPaid && (
          <Link to="/recharge" className="mt-3 inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 transition-colors">
            Pay Registration Fee to Continue
          </Link>
        )}
      </div>
    </div>
  );
});

function StudentChat() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [quota, setQuota] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [mindMapIdx, setMindMapIdx] = useState(null);
  const [infographicIdx, setInfographicIdx] = useState(null);
  const [reviewListContent, setReviewListContent] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(null);
  const [retryLoading, setRetryLoading] = useState(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [speakingPaused, setSpeakingPaused] = useState(false);
  const [voiceMode, setVoiceMode] = useState(null); // null | 'listening' | 'recording' | 'transcribing'
  const voiceSessionRef = useRef(null);
  const [imageMindMapLoading, setImageMindMapLoading] = useState(null);
  const [imageMindMapData, setImageMindMapData] = useState(null);
  const imageMindMapCache = useRef({});
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(false);
  const reviewCache = useRef({});
  const reviewContentRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const isTrainerOrAdmin = user?.role === 'trainer' || user?.role === 'admin';

  const handleCopy = useCallback((idx, content) => {
    navigator.clipboard.writeText(content).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = content;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    });
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  }, []);

  const handleMindMap = useCallback((idx) => {
    setMindMapIdx(idx);
  }, []);

  // Re-ask the question that produced message[idx] using a specific memory technique.
  // The server treats this as a normal chat message (1 question), but the prepended
  // instruction forces the chosen technique for the retry answer.
  const handleTryTechnique = useCallback(async (idx, technique) => {
    if (retryLoading !== null || isLoading) return;
    // Walk back from the assistant message to find the student's original question.
    let userQuestion = '';
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user' && messages[i].content) {
        userQuestion = messages[i].content;
        break;
      }
    }
    if (!userQuestion) {
      toast.error('Could not find the original question to re-teach.');
      return;
    }
    // Mental Summary Chart has its own pictogram pipeline on the technique page
    // (dedicated "Mental Summary" button). Send the student there with the question
    // pre-filled so they get the full MSC flow instead of a plain chat reply.
    if (technique.key === 'mental-summary-charts') {
      navigate('/techniques/mental-summary-charts', { state: { prefilledQuestion: userQuestion } });
      return;
    }
    const prompt = `${technique.prompt}\n\nQuestion: ${userQuestion}`;
    setRetryLoading(idx);
    setMessages(prev => [...prev, {
      role: 'user',
      content: `🔁 Re-teach with ${technique.label}`,
      isRetry: true,
    }]);
    try {
      const response = await chatApi.sendMessage(prompt, conversationId, []);
      const { message, conversationId: convId, quota: newQuota } = response.data;
      setConversationId(convId);
      setMessages(prev => [...prev, { role: 'assistant', content: message, technique: technique.key, userQuery: userQuestion }]);
      setQuota(newQuota);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to re-teach';
      const errorCode = error.response?.data?.code;
      if (errorCode === 'QUOTA_EXHAUSTED' || errorCode === 'DAILY_LIMIT_REACHED' || errorCode === 'TRIAL_EXPIRED') {
        setMessages(prev => [...prev, {
          role: 'system',
          content: !user?.registrationPaid
            ? 'You\'ve used all your free trial questions! Pay the registration fee to unlock unlimited learning.'
            : errorMsg,
          isError: true,
          code: errorCode,
          registrationPaid: !!user?.registrationPaid,
        }]);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setRetryLoading(null);
    }
  }, [messages, conversationId, isLoading, retryLoading, user?.registrationPaid]);

  // Visual Mind Map costs 1 question. We charge the user once per message (using a Set to remember
  // which message indices have already been paid for in this session), then open the client-side modal.
  const chargedVisualRef = useRef(new Set());
  const handleInfographic = useCallback(async (idx) => {
    if (chargedVisualRef.current.has(idx)) {
      setInfographicIdx(idx);
      return;
    }
    try {
      const res = await chatApi.chargeVisualMindMap();
      if (res.data?.quota) setQuota(res.data.quota);
      chargedVisualRef.current.add(idx);
      setInfographicIdx(idx);
    } catch (err) {
      const msg = err.response?.data?.error || 'Could not open Visual Mind Map';
      toast.error(msg);
    }
  }, []);

  const handleSpeak = useCallback((idx, content, action = 'start') => {
    // Toggle pause/resume if same message is already being spoken
    if (speakingIdx === idx && action === 'pause') {
      if (speakingPaused) {
        ttsResume();
        setSpeakingPaused(false);
      } else {
        ttsPause();
        setSpeakingPaused(true);
      }
      return;
    }
    if (speakingIdx === idx && action === 'stop') {
      ttsStop();
      setSpeakingIdx(null);
      setSpeakingPaused(false);
      return;
    }
    // If clicking same message and action=start (already speaking), stop it
    if (speakingIdx === idx) {
      ttsStop();
      setSpeakingIdx(null);
      setSpeakingPaused(false);
      return;
    }
    // Check if user has text selected within this message — read selection only
    const messageEl = document.querySelector(`[data-msg-idx="${idx}"]`);
    const selectedText = getSelectedTextWithin(messageEl);
    const textToSpeak = selectedText || content;
    const started = ttsSpeak(textToSpeak, {
      onEnd: () => { setSpeakingIdx(null); setSpeakingPaused(false); },
    });
    if (started) {
      setSpeakingIdx(idx);
      setSpeakingPaused(false);
      if (selectedText) {
        toast.success('Reading selected text...', { duration: 2000 });
      }
    }
  }, [speakingIdx, speakingPaused]);

  // Stop speech when leaving the page
  useEffect(() => {
    return () => {
      ttsStop();
      if (voiceSessionRef.current) {
        try { voiceSessionRef.current.cancel?.() || voiceSessionRef.current.stop?.(); } catch (e) {}
      }
    };
  }, []);

  const handleVoiceToggle = useCallback(() => {
    // If currently active, stop
    if (voiceMode && voiceSessionRef.current) {
      if (voiceMode === 'recording') {
        voiceSessionRef.current.stop(); // triggers upload to Whisper
        setVoiceMode('transcribing');
      } else {
        voiceSessionRef.current.stop?.();
        setVoiceMode(null);
      }
      return;
    }

    // Start new session — prefer browser STT
    if (hasBrowserSTT()) {
      setVoiceMode('listening');
      const session = startBrowserSTT({
        onInterim: (text) => setInput(text),
        onError: (err) => {
          toast.error(err === 'not-allowed' ? 'Mic permission denied' : 'Voice input failed');
          setVoiceMode(null);
          voiceSessionRef.current = null;
        },
        onEnd: ({ finalText }) => {
          if (finalText) setInput(finalText);
          setVoiceMode(null);
          voiceSessionRef.current = null;
        }
      });
      voiceSessionRef.current = session;
    } else if (hasMediaRecorder()) {
      setVoiceMode('recording');
      const session = startWhisperRecording({
        onRecordingStart: () => {},
        onRecordingStop: () => setVoiceMode('transcribing'),
        onTranscribed: (text) => {
          if (text) setInput((prev) => (prev ? prev + ' ' : '') + text);
          setVoiceMode(null);
          voiceSessionRef.current = null;
        },
        onError: (err) => {
          toast.error(err || 'Voice input failed');
          setVoiceMode(null);
          voiceSessionRef.current = null;
        }
      });
      voiceSessionRef.current = session;
    } else {
      toast.error('Voice input not supported on this browser');
    }
  }, [voiceMode]);

  const handleImageMindMap = useCallback(async (idx, content) => {
    if (imageMindMapCache.current[idx]) {
      setImageMindMapData(imageMindMapCache.current[idx]);
      return;
    }
    try {
      setImageMindMapLoading(idx);
      toast.loading('Generating visual mind map (15-30 sec)...', { id: 'img-mm', duration: 60000 });
      const res = await chatApi.generateMindMapImage(content);
      const data = res.data;
      imageMindMapCache.current[idx] = data;
      setImageMindMapData(data);
      // Visual mind map costs 1 extra question; sync the banner with the server's new quota.
      if (data.quota) setQuota(data.quota);
      toast.success('Mind map ready!', { id: 'img-mm' });
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to generate image mind map';
      toast.error(msg, { id: 'img-mm' });
    } finally {
      setImageMindMapLoading(null);
    }
  }, []);

  const handleDownloadImageMindMap = useCallback(async () => {
    if (!imageMindMapData?.image) return;
    const filename = `mindmap-${(imageMindMapData.topic || 'visual').replace(/\s+/g, '-').toLowerCase()}.png`;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    try {
      // Convert base64 dataURL to Blob
      const res = await fetch(imageMindMapData.image);
      const blob = await res.blob();

      if (isMobile && navigator.share && navigator.canShare) {
        // Use native share sheet — gives user "Save to Photos" / "Save to Gallery" option
        const file = new File([blob], filename, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: 'Visual Mind Map',
              text: imageMindMapData.topic || 'Mind Map'
            });
            return;
          } catch (shareErr) {
            // User cancelled or share failed — fall through to anchor download
            if (shareErr.name === 'AbortError') return;
          }
        }
      }

      // Fallback: standard anchor download (desktop, or mobile without Web Share API)
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error('Download failed');
    }
  }, [imageMindMapData]);

  const handleReviewList = useCallback(async (idx, content) => {
    // Use cached result if available
    if (reviewCache.current[idx]) {
      setReviewListContent(reviewCache.current[idx]);
      return;
    }
    try {
      setReviewLoading(idx);
      const response = await chatApi.generateReviewList(content);
      const reviewList = response.data.reviewList;
      reviewCache.current[idx] = reviewList;
      setReviewListContent(reviewList);
    } catch (error) {
      toast.error('Failed to generate review list');
    } finally {
      setReviewLoading(null);
    }
  }, []);

  const handleDownloadReviewList = useCallback(async () => {
    if (!reviewContentRef.current) return;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    try {
      toast.loading('Generating image...', { id: 'rl-dl' });
      const html2canvas = (await import('html2canvas')).default;

      // Render the table at a fixed comfortable width (900px) regardless of viewport.
      // On mobile the modal is ~340px wide; capturing it directly produces a tall, single-
      // column-wide image. Clone the content into an off-screen wide container, capture
      // that, then clean up — gives a properly laid-out PNG even on narrow phones.
      const clone = reviewContentRef.current.cloneNode(true);
      const stage = document.createElement('div');
      stage.style.cssText = [
        'position: fixed',
        'left: -10000px',
        'top: 0',
        'width: 900px',
        'background: #ffffff',
        'padding: 24px',
        'box-sizing: border-box',
        'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        'color: #111827',
      ].join(';');
      // Strip the modal padding utility classes from the clone (we set our own padding above)
      // and force the table to its natural full width.
      clone.style.cssText = 'width: 100%; max-width: none; overflow: visible;';
      stage.appendChild(clone);
      document.body.appendChild(stage);

      // Force every table cell to wrap normally so columns spread instead of stacking.
      stage.querySelectorAll('table').forEach((t) => {
        t.style.width = '100%';
        t.style.tableLayout = 'auto';
        t.style.borderCollapse = 'collapse';
      });
      stage.querySelectorAll('th, td').forEach((c) => {
        c.style.whiteSpace = 'normal';
        c.style.wordBreak = 'normal';
        c.style.overflowWrap = 'anywhere';
        c.style.verticalAlign = 'top';
      });

      let canvas;
      try {
        canvas = await html2canvas(stage, {
          backgroundColor: '#ffffff',
          scale: 2,
          logging: false,
          useCORS: true,
          width: 900,
          windowWidth: 900,
        });
      } finally {
        document.body.removeChild(stage);
      }
      const dataUrl = canvas.toDataURL('image/png');
      if (!dataUrl || dataUrl === 'data:,') { toast.error('Failed to generate image', { id: 'rl-dl' }); return; }

      if (!isMobile) {
        // Desktop: anchor download with data URL
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = 'review-list.png';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => document.body.removeChild(a), 3000);
        toast.success('Downloaded!', { id: 'rl-dl' });
      } else {
        // Mobile/WebView: save to server, then share or anchor download
        toast.dismiss('rl-dl');
        const res = await fetch('/api/download/image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: dataUrl, filename: 'review-list.png' })
        });
        const { url } = await res.json();
        const serverFileUrl = window.location.origin + url;

        // Try navigator.share with file from server
        let shared = false;
        try {
          const fileRes = await fetch(serverFileUrl);
          const blob = await fileRes.blob();
          const file = new File([blob], 'review-list.png', { type: blob.type });
          if (navigator.share && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: 'Review List' });
            shared = true;
          }
        } catch (e) {
          if (e.name === 'AbortError') shared = true; // user cancelled, that's fine
        }

        if (!shared) {
          // Fallback: anchor download from server URL
          const a = document.createElement('a');
          a.href = serverFileUrl;
          a.download = 'review-list.png';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          setTimeout(() => document.body.removeChild(a), 3000);
        }
      }
    } catch (err) {
      console.error('Review list download error:', err);
      toast.error('Failed to download review list', { id: 'rl-dl' });
    }
  }, []);

  useEffect(() => {
    loadQuota();
    loadConversations();
    // Sync user profile from server to keep auth store in sync with DB
    authApi.getMe().then(res => {
      const serverUser = res.data.user;
      useAuthStore.setState(state => ({
        user: { ...state.user, onboardingCompleted: serverUser.onboardingCompleted, registrationPaid: serverUser.registrationPaid, tier: serverUser.tier }
      }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadQuota = async () => {
    try {
      const response = await chatApi.getQuota();
      setQuota(response.data);
    } catch (error) {
      console.error('Failed to load quota:', error);
    }
  };

  const loadConversations = async () => {
    try {
      setLoadingHistory(true);
      const response = await chatApi.getConversations(50, 0);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadConversation = async (convId) => {
    try {
      setLoadingConversation(true);
      const response = await chatApi.getConversation(convId);
      const { messages: convMessages } = response.data;
      setMessages(convMessages.map(m => {
        const msg = { role: m.role, content: m.content };
        if (m.attachments && m.attachments.length > 0) {
          msg.images = m.attachments
            .filter(a => a.file_type === 'image')
            .map(a => ({ name: a.original_name, url: `/uploads/${a.file_path}` }));
          msg.pdfNames = m.attachments
            .filter(a => a.file_type === 'pdf')
            .map(a => a.original_name);
        }
        return msg;
      }));
      setConversationId(convId);
      setSelectedFiles([]);
      setShareLink(null);
      setShowHistory(false);
    } catch (error) {
      toast.error('Failed to load conversation');
    } finally {
      setLoadingConversation(false);
    }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this conversation?')) return;
    try {
      await chatApi.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        startNewChat();
      }
      toast.success('Conversation deleted');
    } catch (error) {
      toast.error('Failed to delete conversation');
    }
  };

  // Inline rename of a chat-history row. `editingConvId` is the id currently being edited;
  // `editingTitle` is the draft text. Commit on Enter/blur, cancel on Escape.
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  const startRenameConversation = (conv, e) => {
    e.stopPropagation();
    setEditingConvId(conv.id);
    setEditingTitle(conv.title || '');
  };

  const cancelRenameConversation = () => {
    setEditingConvId(null);
    setEditingTitle('');
  };

  const commitRenameConversation = async () => {
    const id = editingConvId;
    const title = editingTitle.trim();
    if (!id) return;
    const original = conversations.find(c => c.id === id)?.title || '';
    setEditingConvId(null);
    setEditingTitle('');
    if (!title || title === original) return;
    // Optimistic update
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
    try {
      await chatApi.renameConversation(id, title);
      toast.success('Renamed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to rename');
      setConversations(prev => prev.map(c => c.id === id ? { ...c, title: original } : c));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isValid = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isSmallEnough = file.size <= 10 * 1024 * 1024; // 10MB
      if (!isValid) toast.error(`${file.name} is not a supported file type`);
      if (!isSmallEnough) toast.error(`${file.name} is too large (max 10MB)`);
      return isValid && isSmallEnough;
    });

    setSelectedFiles(prev => [...prev, ...validFiles].slice(0, 5)); // Max 5 files
    e.target.value = ''; // Reset input
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!input.trim() && selectedFiles.length === 0) || isLoading) return;

    const userMessage = input.trim();
    const filesToUpload = [...selectedFiles];
    setInput('');
    setIsLoading(true);

    // Create image previews for immediate display
    const imagePreviews = filesToUpload
      .filter(f => f.type.startsWith('image/'))
      .map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
    const pdfNames = filesToUpload
      .filter(f => !f.type.startsWith('image/'))
      .map(f => f.name);

    const msgIndex = messages.length;
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage || (filesToUpload.length > 0 ? '' : ''),
      images: imagePreviews,
      pdfNames
    }]);

    try {
      // Upload files first if any
      let fileIds = [];
      if (filesToUpload.length > 0) {
        setIsUploading(true);
        const uploadResponse = await uploadApi.uploadFiles(filesToUpload);
        fileIds = uploadResponse.data.files.map(f => f.id);

        // Replace blob previews with server URLs
        const serverImages = uploadResponse.data.files
          .filter(f => f.fileType === 'image')
          .map(f => ({ name: f.originalName, url: f.fileUrl }));
        if (serverImages.length > 0) {
          setMessages(prev => prev.map((m, i) =>
            i === msgIndex ? { ...m, images: serverImages } : m
          ));
        }

        setSelectedFiles([]);
        setIsUploading(false);
      }

      // Send message with file IDs
      const response = await chatApi.sendMessage(userMessage || 'Please analyze the attached file(s).', conversationId, fileIds);
      const { message, conversationId: convId, quota: newQuota } = response.data;

      const isNewConversation = !conversationId;
      setConversationId(convId);
      setMessages(prev => [...prev, { role: 'assistant', content: message }]);
      setQuota(newQuota);

      // Refresh conversation list if new conversation was created
      if (isNewConversation) {
        loadConversations();
      }

      // Show onboarding prompt for paid users who haven't completed their profile
      if (user?.registrationPaid && !user?.onboardingCompleted) {
        setShowOnboardingPrompt(true);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to send message';
      const errorCode = error.response?.data?.code;

      if (errorCode === 'QUOTA_EXHAUSTED' || errorCode === 'DAILY_LIMIT_REACHED' || errorCode === 'TRIAL_EXPIRED') {
        setMessages(prev => [...prev, {
          role: 'system',
          content: !user?.registrationPaid
            ? 'You\'ve used all your free trial questions! Pay the registration fee to unlock unlimited learning.'
            : errorMsg,
          isError: true,
          code: errorCode,
          registrationPaid: !!user?.registrationPaid
        }]);
      } else {
        toast.error(errorMsg);
        // Remove the user message if failed
        setMessages(prev => prev.slice(0, -1));
      }
      setSelectedFiles([]);
    } finally {
      setIsLoading(false);
      setIsUploading(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setSelectedFiles([]);
    setShareLink(null);
    setShowHistory(false);
  };

  const handleShare = async () => {
    if (!conversationId) {
      toast.error('No conversation to share');
      return;
    }

    try {
      const response = await shareApi.createShare(conversationId);
      setShareLink(response.data);
      setShowShareModal(true);
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  const copyShareLink = () => {
    if (shareLink) {
      const fullUrl = window.location.origin + shareLink.shareUrl;
      navigator.clipboard.writeText(fullUrl);
      toast.success('Link copied to clipboard');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const renderQuotaInfo = () => {
    if (!quota) return null;

    if (quota.tier === 'L1') {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-amber-800 font-medium">Free Trial</span>
              <span className="text-amber-600 text-sm ml-2">
                {quota.totalLimit
                  ? `${quota.questionsRemaining} free questions remaining`
                  : `${quota.questionsRemainingToday} free questions left today`}
              </span>
            </div>
            <Link to="/recharge" className="text-primary-600 text-sm font-medium hover:underline">
              Upgrade
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/30 rounded-lg p-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-primary-800 dark:text-primary-200">
              <span className="font-medium">{quota.availableQuestions}</span> questions available
            </span>
            {quota.nextResetDate && (
              <span className="text-primary-600 dark:text-primary-400 text-xs ml-2">
                Resets on: {new Date(quota.nextResetDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
          {quota.availableQuestions < 20 && (
            <Link to="/recharge" className="text-primary-600 text-sm hover:underline">
              Recharge now
            </Link>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex">
      {/* Chat History Sidebar */}
      <div className={`${showHistory ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:relative z-30 h-full w-72 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col transition-transform duration-200`}>
        <div className="p-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex items-center justify-between">
          <h2 className="font-semibold text-gray-700 dark:text-gray-200 text-sm">Chat History</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={startNewChat}
              className="p-2 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/15 rounded-lg transition-colors"
              title="New Chat"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowHistory(false)}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg md:hidden"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center py-8 px-4">
              <MessageSquare className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-gray-400 dark:text-gray-500 text-sm">No conversations yet</p>
            </div>
          ) : (
            <div className="py-1">
              {conversations.map((conv) => {
                const isEditing = editingConvId === conv.id;
                return (
                <div
                  key={conv.id}
                  onClick={() => !isEditing && loadConversation(conv.id)}
                  className={`group flex items-center px-3 py-2.5 mx-1 rounded-lg transition-colors ${isEditing ? '' : 'cursor-pointer'} ${
                    conversationId === conv.id
                      ? 'bg-primary-100 dark:bg-primary-500/20 text-primary-800 dark:text-primary-200'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <MessageSquare className="w-4 h-4 shrink-0 mr-2 opacity-50" />
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        autoFocus
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={commitRenameConversation}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); commitRenameConversation(); }
                          else if (e.key === 'Escape') { e.preventDefault(); cancelRenameConversation(); }
                        }}
                        maxLength={200}
                        className="w-full text-sm px-1.5 py-0.5 bg-white dark:bg-gray-900 border border-primary-400 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    ) : (
                      <p className="text-sm truncate">{conv.title || 'New conversation'}</p>
                    )}
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {formatDate(conv.updated_at || conv.created_at)}
                      {conv.questions_count > 0 && ` · ${conv.questions_count} msg`}
                    </p>
                  </div>
                  {!isEditing && (
                    <>
                      <button
                        onClick={(e) => startRenameConversation(conv, e)}
                        className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 dark:text-gray-500 hover:text-primary-600 transition-all"
                        title="Rename"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => deleteConversation(conv.id, e)}
                        className="p-1 opacity-0 group-hover:opacity-100 text-gray-400 dark:text-gray-500 hover:text-red-500 transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Overlay for mobile when sidebar is open */}
      {showHistory && (
        <div
          className="fixed inset-0 bg-black/30 z-20 md:hidden"
          onClick={() => setShowHistory(false)}
        />
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-950">
        {/* Header */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                onClick={() => window.dispatchEvent(new Event('open-sidebar'))}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg lg:hidden"
                title="Menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowHistory(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg md:hidden"
                title="Chat History"
              >
                <MessageSquare className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold hidden sm:block dark:text-gray-100">Memory Coach</h1>
            </div>
            <div className="flex items-center space-x-2">
              {conversationId && isTrainerOrAdmin && (
                <button
                  onClick={handleShare}
                  className="flex items-center text-sm text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-1"
                  title="Share conversation"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Share
                </button>
              )}
              <button
                onClick={startNewChat}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                New Chat
              </button>
            </div>
          </div>
          {renderQuotaInfo()}
        </div>

        {/* Loading conversation overlay */}
        {loadingConversation && (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
          </div>
        )}

        {/* Messages */}
        {!loadingConversation && (
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <h2 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Welcome to Memory Coach!
                </h2>
                <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  I'm your AI memory tutor. Ask me anything about memory techniques,
                  or give me something to help you memorize!
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-3">
                  You can also attach images or PDFs for me to help you memorize.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {[
                    'How does the Memory Palace work?',
                    'Help me memorize the periodic table',
                    'What is spaced repetition?'
                  ].map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => setInput(suggestion)}
                      className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                msg={msg}
                idx={idx}
                copiedIdx={copiedIdx}
                onCopy={handleCopy}
                onMindMap={handleMindMap}
                onInfographic={handleInfographic}
                onReviewList={handleReviewList}
                reviewLoading={reviewLoading}
                speakingIdx={speakingIdx}
                speakingPaused={speakingPaused}
                onSpeak={handleSpeak}
                onImageMindMap={handleImageMindMap}
                imageMindMapLoading={imageMindMapLoading}
                onTryTechnique={handleTryTechnique}
                retryLoading={retryLoading}
              />
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full loading-dot"></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full loading-dot"></div>
                    <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full loading-dot"></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm dark:text-gray-200"
                >
                  {file.type.startsWith('image/') ? (
                    <Image className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2 text-red-600 dark:text-red-400" />
                  )}
                  <span className="truncate max-w-[150px]">{file.name}</span>
                  <button
                    onClick={() => removeFile(idx)}
                    className="ml-2 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="flex items-end space-x-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf"
              multiple
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-3 text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/15 rounded-xl transition-colors shrink-0"
              title="Attach files (images or PDFs)"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <textarea
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if ((input.trim() || selectedFiles.length > 0) && !isLoading && !isUploading) handleSubmit(e);
                }
              }}
              placeholder={selectedFiles.length > 0
                ? "Add a message about the files..."
                : "Ask me anything about memory techniques..."
              }
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none overflow-y-auto"
              style={{ minHeight: '48px', maxHeight: '150px' }}
              rows={1}
              disabled={isLoading || isUploading}
            />
            {isVoiceInputSupported() && (
              <button
                type="button"
                onClick={handleVoiceToggle}
                disabled={isLoading || isUploading || voiceMode === 'transcribing'}
                className={`p-3 rounded-xl transition-colors shrink-0 ${
                  voiceMode === 'listening' || voiceMode === 'recording'
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 animate-pulse'
                    : voiceMode === 'transcribing'
                      ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                      : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-500/15'
                }`}
                title={
                  voiceMode === 'listening' ? 'Stop listening'
                  : voiceMode === 'recording' ? 'Stop recording'
                  : voiceMode === 'transcribing' ? 'Transcribing...'
                  : 'Voice input'
                }
              >
                {voiceMode === 'transcribing' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : voiceMode === 'listening' || voiceMode === 'recording' ? (
                  <MicOff className="w-5 h-5" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading || isUploading || (!input.trim() && selectedFiles.length === 0)}
              className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              {isLoading || isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Share Modal */}
      {showShareModal && shareLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Share Conversation</h2>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Share Link
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={window.location.origin + shareLink.shareUrl}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                  />
                  <button
                    onClick={copyShareLink}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="text-sm text-gray-500">
                <p>Access: <span className="font-medium capitalize">{shareLink.accessType}</span></p>
                <p>Views: {shareLink.viewCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mind Map Modal */}
      {mindMapIdx !== null && messages[mindMapIdx] && (
        <Suspense fallback={null}>
          <MindMapModal
            markdown={messages[mindMapIdx].content}
            title={(() => {
              for (let i = mindMapIdx - 1; i >= 0; i--) {
                if (messages[i]?.role === 'user' && messages[i].content) return messages[i].content;
              }
              return '';
            })()}
            onClose={() => setMindMapIdx(null)}
          />
        </Suspense>
      )}

      {/* Infographic Notes Mind Map Modal */}
      {infographicIdx !== null && messages[infographicIdx] && (
        <Suspense fallback={null}>
          <InfographicMindMap
            markdown={messages[infographicIdx].content}
            onClose={() => setInfographicIdx(null)}
          />
        </Suspense>
      )}

      {/* Image Mind Map Modal */}
      {imageMindMapData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setImageMindMapData(null)}>
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Palette className="w-5 h-5 mr-2 text-pink-600" /> Visual Mind Map
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleDownloadImageMindMap}
                  className="flex items-center px-3 py-1.5 text-sm bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <Download className="w-4 h-4 mr-1" /> Download
                </button>
                <button onClick={() => setImageMindMapData(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4">
              <img src={imageMindMapData.image} alt="Visual Mind Map" className="w-full h-auto rounded-lg border border-gray-200" />
              {imageMindMapData.topic && (
                <p className="text-center text-sm text-gray-500 mt-3">Topic: <span className="font-medium text-gray-700">{imageMindMapData.topic}</span></p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review List Modal */}
      {reviewListContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setReviewListContent(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ClipboardList className="w-5 h-5 mr-2 text-emerald-600" /> Review List
              </h3>
              <div className="flex items-center space-x-1">
                <button onClick={handleDownloadReviewList} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" title="Download as PNG">
                  <Download className="w-4 h-4" />
                </button>
                <button onClick={() => setReviewListContent(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div ref={reviewContentRef} className="p-4 prose prose-sm max-w-none prose-table:my-0 overflow-x-auto
              [&_table]:w-full [&_table]:border-collapse [&_table]:border [&_table]:border-gray-200
              [&_th]:bg-emerald-50 [&_th]:text-emerald-800 [&_th]:font-semibold [&_th]:px-3 [&_th]:py-2 [&_th]:border [&_th]:border-gray-200 [&_th]:text-left [&_th]:text-sm
              [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-gray-200 [&_td]:text-sm [&_td]:text-gray-700
              [&_tr:nth-child(even)]:bg-gray-50">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{
                (() => {
                  // Strip leading heading line (📋 **Review List** etc.)
                  let text = reviewListContent.replace(/^.*?(review\s*list).*?\n/i, '').trim();
                  const lines = text.split('\n');
                  const fixed = [];
                  let headerColCount = 0;
                  let hasSeparator = false;

                  for (let i = 0; i < lines.length; i++) {
                    let line = lines[i].trim();
                    if (!line) continue;

                    // Skip non-table lines (bullets, checkboxes, plain text without pipes)
                    if (!line.includes('|')) continue;

                    // Ensure line starts and ends with pipe
                    if (!line.startsWith('|')) line = '| ' + line;
                    if (!line.endsWith('|')) line = line + ' |';

                    // Check if this is a separator row (contains only |, -, :, spaces)
                    const isSeparator = line.replace(/[|\s:-]/g, '').length === 0 && line.includes('-');

                    if (isSeparator) {
                      // Rebuild separator with correct column count
                      const cols = headerColCount || 3;
                      fixed.push('| ' + Array(cols).fill('---').join(' | ') + ' |');
                      hasSeparator = true;
                    } else {
                      // Count columns from this row
                      const colCount = line.split('|').filter(c => c.trim() !== '').length;
                      if (!headerColCount && colCount > 0) headerColCount = colCount;
                      fixed.push(line);

                      // If this is the first data row and no separator yet, insert one
                      if (fixed.length === 1 && !hasSeparator) {
                        const cols = colCount || 3;
                        fixed.push('| ' + Array(cols).fill('---').join(' | ') + ' |');
                        hasSeparator = true;
                      }
                    }
                  }

                  // If still no separator after header, add one
                  if (fixed.length >= 1 && !hasSeparator) {
                    const cols = headerColCount || 3;
                    fixed.splice(1, 0, '| ' + Array(cols).fill('---').join(' | ') + ' |');
                  }

                  return fixed.join('\n');
                })()
              }</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
      {/* Onboarding Prompt Modal */}
      {showOnboardingPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🧠</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Complete Your Profile</h3>
            <p className="text-gray-600 mb-6">
              Set up your learning profile so we can personalize stories using your Memory Palace locations and learning style!
            </p>
            <button
              onClick={() => { setShowOnboardingPrompt(false); navigate('/onboarding'); }}
              className="w-full py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors text-sm"
            >
              Complete Profile
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentChat;
